import React, { useMemo, useState } from "react";
import {
    Drawer,
    Table,
    Pagination,
    Spin,
    Tag,
    Typography,
    Button,
    notification,
} from "antd";
import {
    ArrowLeftOutlined,
    CopyOutlined,
    FilterOutlined,
    CalendarOutlined,
    SwapOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { getFormattedAmount, formatSmartPrice } from "Shared/Utils";
import getCurrencySymbol from "Shared/CurrancySymbol";
import { Tokens } from "Shared/Tokens";
import TradebookEventRow, {
    formatDateGroupKey,
    formatQty,
} from "Components/Shared/TradebookEventRow";

const { Text } = Typography;

const BinanceTransactions = (props) => {
    const {
        open,
        onClose,
        asset,
        transactions = [],
        loading = false,
        pagination = {
            total: 0,
            page: 1,
            limit: 20,
            totalPages: 0,
            hasMore: false,
        },
        onPageChange,
        onDaysChange,
        days = 7,
        cutoffDate,
        isMobile = false,
        isTablet = false,
        currency = "USDT",
    } = props;

    const [sideFilter, setSideFilter] = useState("ALL");
    const [accountTypeFilter, setAccountTypeFilter] = useState("ALL");
    const [selectedDays, setSelectedDays] = useState(days);
    const [sortOrder, setSortOrder] = useState("desc");
    const [localPage, setLocalPage] = useState(1);

    const symbol = asset?.asset || asset?.name || "Asset";
    const currencySymbol = getCurrencySymbol(currency);

    const formatCryptoAmount = (amount) => {
        if (!amount || amount === 0) return "0";
        const num = Number(amount);
        if (Number.isNaN(num)) return "0";
        const formatted = num.toFixed(9);
        return formatted.replace(/\.?0+$/, "");
    };

    const copyToClipboard = (text, label) => {
        if (!text) return;
        navigator.clipboard.writeText(String(text));
        notification.success({
            message: "Copied",
            description: `${label} copied to clipboard`,
            duration: 2,
        });
    };

    const handleDaysSelect = (newDays) => {
        setSelectedDays(newDays);
        setLocalPage(1);
        if (onDaysChange) {
            onDaysChange(newDays);
        }
    };

    const totalBuyVolume = transactions
        .filter((t) => t.side === "BUY")
        .reduce((sum, t) => sum + (Number(t.quoteQuantity) || 0), 0);

    const totalSellVolume = transactions
        .filter((t) => t.side === "SELL")
        .reduce((sum, t) => sum + (Number(t.quoteQuantity) || 0), 0);

    const totalVolume = totalBuyVolume + totalSellVolume;
    const netFlow = totalBuyVolume - totalSellVolume;

    const enrichedTransactions = useMemo(() => {
        const toMs = (raw) => {
            const num = typeof raw === "number" ? raw : parseInt(raw, 10);
            if (!Number.isFinite(num)) return 0;
            return num > 1e12 ? num : num * 1000;
        };
        const sortedAsc = [...transactions]
            .map((t, idx) => ({
                ...t,
                _normalizedMs: toMs(t.time),
                _origIdx: idx,
            }))
            .sort((a, b) => a._normalizedMs - b._normalizedMs);

        let runningQty = 0;
        let runningCost = 0;
        const withRunning = sortedAsc.map((t) => {
            const qty = Math.max(0, Number(t.quantity || 0));
            const quote = Math.max(0, Number(t.quoteQuantity || 0));
            const isSell = String(t.side || "").toUpperCase() === "SELL";
            const beforeQty = runningQty;
            const beforeCost = runningCost;
            let realizedPlDelta = 0;

            if (isSell) {
                const avgCost = beforeQty > 1e-12 ? beforeCost / beforeQty : 0;
                const costReleased = Math.min(beforeCost, qty * avgCost);
                realizedPlDelta = quote - costReleased;
                runningQty = Math.max(0, beforeQty - qty);
                runningCost = Math.max(0, beforeCost - costReleased);
            } else {
                runningQty = beforeQty + qty;
                runningCost = beforeCost + quote;
            }

            const afterCostBasis = Number(runningCost.toFixed(2));
            return {
                ...t,
                beforeQty: Number(beforeQty.toFixed(8)),
                afterQty: Number(runningQty.toFixed(8)),
                beforeCostBasis: Number(beforeCost.toFixed(2)),
                afterCostBasis,
                runningTotalInvested: afterCostBasis,
                realizedPlDelta: Number(realizedPlDelta.toFixed(2)),
                coin: symbol,
                id: t.tradeId || t.id || t.orderId || `tx-${t._origIdx}`,
                type: isSell ? "sell" : "buy",
                source: String(t.accountType || t.type || "spot").toLowerCase(),
                origin: "binance",
                qty,
                unitPrice: Number(t.price || 0),
                quoteQty: quote,
            };
        });

        if (sortOrder === "desc") {
            withRunning.reverse();
        }
        return withRunning;
    }, [transactions, symbol, sortOrder]);

    const filteredTransactions = useMemo(
        () =>
            enrichedTransactions.filter((t) => {
                if (sideFilter !== "ALL" && t.side !== sideFilter) return false;
                const typeValue = (
                    t.accountType ||
                    t.type ||
                    "spot"
                ).toUpperCase();
                if (
                    accountTypeFilter !== "ALL" &&
                    typeValue !== accountTypeFilter
                )
                    return false;
                return true;
            }),
        [enrichedTransactions, sideFilter, accountTypeFilter]
    );

    const effectivePageSize = Number(pagination?.limit) || 20;
    const isClientPaginated =
        filteredTransactions.length > effectivePageSize &&
        (!pagination?.total || pagination.total <= filteredTransactions.length);
    const activePage = isClientPaginated ? localPage : pagination?.page || 1;
    const activeTotal = isClientPaginated
        ? filteredTransactions.length
        : Math.max(Number(pagination?.total) || 0, filteredTransactions.length);

    const displayedTransactions = useMemo(() => {
        if (!isClientPaginated) return filteredTransactions;
        const startIdx = (localPage - 1) * effectivePageSize;
        return filteredTransactions.slice(
            startIdx,
            startIdx + effectivePageSize
        );
    }, [filteredTransactions, isClientPaginated, localPage, effectivePageSize]);

    const mobileDateGroups = useMemo(() => {
        const groups = [];
        const groupMap = new Map();
        displayedTransactions.forEach((ev) => {
            const label = formatDateGroupKey(ev._normalizedMs || ev.time);
            if (!groupMap.has(label)) {
                const group = { dateLabel: label, items: [] };
                groupMap.set(label, group);
                groups.push(group);
            }
            groupMap.get(label).items.push(ev);
        });
        return groups;
    }, [displayedTransactions]);

    const timeHorizonOptions = [
        { label: "1d", value: 1 },
        { label: "7d", value: 7 },
        { label: "30d", value: 30 },
        { label: "90d", value: 90 },
        { label: "1y", value: 365 },
        { label: "All", value: "all" },
    ];

    const transactionColumns = [
        {
            title: "Time",
            dataIndex: "time",
            key: "time",
            render: (time) => {
                const timeValue =
                    typeof time === "number" ? time : parseInt(time, 10);
                const date = dayjs(
                    timeValue > 1e12 ? timeValue : timeValue * 1000
                );
                const formattedTime = isMobile
                    ? date.format("MMM DD, HH:mm")
                    : date.format("MMM DD, YYYY hh:mm:ss A");

                return (
                    <Text
                        style={{
                            color: Tokens.textSecondary,
                            fontSize: isMobile ? "11px" : "13px",
                            fontVariantNumeric: "tabular-nums",
                            whiteSpace: "nowrap",
                        }}
                        title={date.format("MMMM DD, YYYY hh:mm:ss A")}
                    >
                        {formattedTime}
                    </Text>
                );
            },
            width: isMobile ? 130 : 190,
            fixed: isMobile ? "left" : false,
        },
        {
            title: "Account",
            dataIndex: "accountType",
            key: "accountType",
            render: (accountType, record) => {
                const typeValue =
                    accountType || record?.type?.toLowerCase() || "spot";
                const normalized = typeValue.toLowerCase();
                const label =
                    normalized.charAt(0).toUpperCase() + normalized.slice(1);
                return (
                    <Tag
                        style={{
                            backgroundColor: Tokens.raised,
                            color: Tokens.textSecondary,
                            border: `1px solid ${Tokens.hairline}`,
                            fontSize: isMobile ? "11px" : "12px",
                            fontWeight: 400,
                            fontFamily: Tokens.fontMono,
                            borderRadius: Tokens.radiusTag,
                            padding: isMobile ? "0 4px" : "2px 8px",
                            margin: 0,
                        }}
                    >
                        {label}
                    </Tag>
                );
            },
            width: isMobile ? 75 : 100,
        },
        {
            title: "Side",
            dataIndex: "side",
            key: "side",
            render: (side) => {
                const isBuy = side === "BUY";
                return (
                    <Tag
                        style={{
                            backgroundColor: isBuy
                                ? Tokens.positiveBg
                                : Tokens.negativeBg,
                            color: isBuy ? Tokens.positive : Tokens.negative,
                            border: `1px solid ${
                                isBuy
                                    ? Tokens.positiveBorder
                                    : Tokens.negativeBorder
                            }`,
                            fontWeight: 500,
                            fontSize: isMobile ? "11px" : "12px",
                            fontFamily: Tokens.fontMono,
                            borderRadius: Tokens.radiusTag,
                            padding: isMobile ? "0 4px" : "2px 8px",
                            margin: 0,
                        }}
                    >
                        {side === "BUY" ? "Buy" : "Sell"}
                    </Tag>
                );
            },
            width: isMobile ? 65 : 85,
        },
        {
            title: "Quantity",
            dataIndex: "quantity",
            key: "quantity",
            align: "right",
            render: (quantity) => (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: "4px",
                    }}
                >
                    <Text
                        style={{
                            color: Tokens.textPrimary,
                            fontFamily: Tokens.fontMono,
                            fontVariantNumeric: "tabular-nums",
                            fontSize: isMobile ? "11px" : "13px",
                        }}
                    >
                        {formatCryptoAmount(quantity)}
                    </Text>
                    <CopyOutlined
                        onClick={() => copyToClipboard(quantity, "Quantity")}
                        style={{
                            color: Tokens.textMuted,
                            cursor: "pointer",
                            fontSize: "12px",
                        }}
                        title="Copy quantity"
                    />
                </div>
            ),
            width: isMobile ? 110 : 140,
        },
        {
            title: "Price",
            dataIndex: "price",
            key: "price",
            align: "right",
            render: (price) => (
                <Text
                    style={{
                        color: Tokens.textSecondary,
                        fontFamily: Tokens.fontMono,
                        fontVariantNumeric: "tabular-nums",
                        fontSize: isMobile ? "11px" : "13px",
                    }}
                >
                    {getCurrencySymbol(currency)}
                    {formatSmartPrice(price)}
                </Text>
            ),
            width: isMobile ? 95 : 120,
        },
        {
            title: "Total",
            dataIndex: "quoteQuantity",
            key: "quoteQuantity",
            align: "right",
            render: (quoteQuantity) => (
                <Text
                    style={{
                        color: Tokens.textPrimary,
                        fontWeight: 500,
                        fontFamily: Tokens.fontMono,
                        fontVariantNumeric: "tabular-nums",
                        fontSize: isMobile ? "11px" : "13px",
                    }}
                >
                    {getCurrencySymbol(currency)}
                    {getFormattedAmount(quoteQuantity, false, 2)}
                </Text>
            ),
            width: isMobile ? 95 : 120,
        },
        {
            title: "Running Bal",
            dataIndex: "afterQty",
            key: "afterQty",
            align: "right",
            render: (afterQty, record) => (
                <Text
                    data-testid="trade-row-running-balance"
                    style={{
                        color: Tokens.textPrimary,
                        fontWeight: 600,
                        fontFamily: Tokens.fontMono,
                        fontVariantNumeric: "tabular-nums",
                        fontSize: isMobile ? "11px" : "12px",
                    }}
                >
                    {formatQty(afterQty ?? record.quantity)} {symbol} ·{" "}
                    {currencySymbol}
                    {getFormattedAmount(record.afterCostBasis || 0, false, 2)}
                </Text>
            ),
            width: isMobile ? 120 : 160,
        },
        {
            title: "Commission",
            dataIndex: "commission",
            key: "commission",
            align: "right",
            render: (commission, record) => (
                <Text
                    style={{
                        color: Tokens.textMuted,
                        fontFamily: Tokens.fontMono,
                        fontVariantNumeric: "tabular-nums",
                        fontSize: isMobile ? "11px" : "13px",
                    }}
                >
                    {formatCryptoAmount(commission)} {record.commissionAsset}
                </Text>
            ),
            width: isMobile ? 110 : 150,
        },
    ];

    return (
        <Drawer
            closable={false}
            push={false}
            title={
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: "8px",
                        width: "100%",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            minWidth: 0,
                        }}
                    >
                        <Button
                            type="text"
                            icon={
                                <ArrowLeftOutlined
                                    style={{
                                        color: Tokens.textSecondary,
                                        fontSize: "16px",
                                    }}
                                />
                            }
                            onClick={onClose}
                            title="Back to asset"
                            style={{
                                padding: 0,
                                width: "32px",
                                height: "32px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                            }}
                        />
                        <Text
                            style={{
                                fontSize: "15px",
                                color: Tokens.textPrimary,
                                fontWeight: 500,
                                whiteSpace: "nowrap",
                            }}
                        >
                            Transactions for {symbol}
                        </Text>
                        {cutoffDate && cutoffDate !== "all" && (
                            <Tag
                                style={{
                                    backgroundColor: Tokens.surface,
                                    color: Tokens.accent,
                                    border: `1px solid ${Tokens.hairline}`,
                                    borderRadius: Tokens.radiusTag,
                                    fontFamily: Tokens.fontMono,
                                    fontSize: "10px",
                                    padding: "0 6px",
                                    margin: 0,
                                }}
                            >
                                Since {cutoffDate}
                            </Tag>
                        )}
                    </div>

                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "4px",
                        }}
                    >
                        <CalendarOutlined
                            style={{
                                color: Tokens.textMuted,
                                fontSize: "12px",
                                marginRight: "4px",
                            }}
                        />
                        {timeHorizonOptions.map((opt) => {
                            const isActive = selectedDays === opt.value;
                            return (
                                <Tag
                                    key={String(opt.value)}
                                    onClick={() => handleDaysSelect(opt.value)}
                                    style={{
                                        backgroundColor: isActive
                                            ? Tokens.raised
                                            : "transparent",
                                        color: isActive
                                            ? Tokens.textPrimary
                                            : Tokens.textSecondary,
                                        border: isActive
                                            ? `1px solid ${Tokens.accent}`
                                            : `1px solid ${Tokens.hairline}`,
                                        borderRadius: Tokens.radiusTag,
                                        cursor: "pointer",
                                        fontSize: "11px",
                                        fontWeight: isActive ? 500 : 400,
                                        padding: "2px 8px",
                                        margin: 0,
                                    }}
                                >
                                    {opt.label}
                                </Tag>
                            );
                        })}
                    </div>
                </div>
            }
            placement={isMobile ? "bottom" : "right"}
            width={(() => {
                if (isMobile) return "100%";
                if (isTablet) return "90%";
                return 720;
            })()}
            height={isMobile ? "88%" : "100%"}
            onClose={onClose}
            open={open}
            headerStyle={{
                backgroundColor: Tokens.bg,
                borderBottom: `1px solid ${Tokens.hairline}`,
            }}
            bodyStyle={{ backgroundColor: Tokens.bg, padding: "16px" }}
            footerStyle={{
                backgroundColor: Tokens.bg,
                borderTop: `1px solid ${Tokens.hairline}`,
                padding:
                    "12px 16px calc(12px + env(safe-area-inset-bottom, 0px))",
            }}
            style={{ zIndex: 1000 }}
            footer={
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Pagination
                        current={activePage}
                        total={activeTotal}
                        pageSize={effectivePageSize}
                        onChange={(page) => {
                            if (isClientPaginated) {
                                setLocalPage(page);
                            } else if (onPageChange) {
                                onPageChange(page);
                            }
                        }}
                        showSizeChanger={false}
                        size="small"
                    />
                    <Button
                        type="default"
                        onClick={onClose}
                        style={{
                            height: "38px",
                            backgroundColor: Tokens.raised,
                            color: Tokens.textPrimary,
                            border: `1px solid ${Tokens.hairline}`,
                            borderRadius: Tokens.radiusControl,
                        }}
                    >
                        Close
                    </Button>
                </div>
            }
        >
            <Spin spinning={loading}>
                {transactions.length > 0 && (
                    <div
                        style={{
                            backgroundColor: Tokens.card,
                            border: `1px solid ${Tokens.hairline}`,
                            borderRadius: Tokens.radiusCard,
                            padding: "12px 16px",
                            marginBottom: "16px",
                            display: "grid",
                            gridTemplateColumns: isMobile
                                ? "1fr 1fr"
                                : "1fr 1fr 1fr 1fr",
                            gap: "12px",
                        }}
                    >
                        <div>
                            <Text
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                }}
                            >
                                Total volume
                            </Text>
                            <div
                                style={{
                                    color: Tokens.textPrimary,
                                    fontWeight: 500,
                                    fontSize: "14px",
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                    marginTop: "2px",
                                }}
                            >
                                {getCurrencySymbol(currency)}
                                {getFormattedAmount(totalVolume, false, 2)}
                            </div>
                        </div>
                        <div>
                            <Text
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                }}
                            >
                                Total bought
                            </Text>
                            <div
                                style={{
                                    color: Tokens.positive,
                                    fontWeight: 500,
                                    fontSize: "14px",
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                    marginTop: "2px",
                                }}
                            >
                                {getCurrencySymbol(currency)}
                                {getFormattedAmount(totalBuyVolume, false, 2)}
                            </div>
                        </div>
                        <div>
                            <Text
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                }}
                            >
                                Total sold
                            </Text>
                            <div
                                style={{
                                    color: Tokens.negative,
                                    fontWeight: 500,
                                    fontSize: "14px",
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                    marginTop: "2px",
                                }}
                            >
                                {getCurrencySymbol(currency)}
                                {getFormattedAmount(totalSellVolume, false, 2)}
                            </div>
                        </div>
                        <div>
                            <Text
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                }}
                            >
                                Net flow
                            </Text>
                            <div
                                style={{
                                    color:
                                        netFlow >= 0
                                            ? Tokens.positive
                                            : Tokens.negative,
                                    fontWeight: 500,
                                    fontSize: "14px",
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                    marginTop: "2px",
                                }}
                            >
                                {netFlow >= 0 ? "+" : "-"}
                                {getCurrencySymbol(currency)}
                                {getFormattedAmount(Math.abs(netFlow), true, 2)}
                            </div>
                        </div>
                    </div>
                )}

                {transactions.length > 0 && (
                    <div
                        style={{
                            display: "flex",
                            gap: "8px",
                            marginBottom: "16px",
                            alignItems: "center",
                            flexWrap: "wrap",
                        }}
                    >
                        <FilterOutlined
                            style={{
                                color: Tokens.textMuted,
                                fontSize: "13px",
                            }}
                        />
                        <div style={{ display: "flex", gap: "4px" }}>
                            {[
                                ["ALL", "All"],
                                ["BUY", "Buy"],
                                ["SELL", "Sell"],
                            ].map(([side, label]) => {
                                const isActive = sideFilter === side;
                                return (
                                    <Tag
                                        key={side}
                                        onClick={() => setSideFilter(side)}
                                        style={{
                                            backgroundColor: isActive
                                                ? Tokens.raised
                                                : "transparent",
                                            color: isActive
                                                ? Tokens.textPrimary
                                                : Tokens.textSecondary,
                                            border: isActive
                                                ? `1px solid ${Tokens.accent}`
                                                : `1px solid ${Tokens.hairline}`,
                                            borderRadius: Tokens.radiusTag,
                                            cursor: "pointer",
                                            fontSize: "11px",
                                            fontWeight: isActive ? 500 : 400,
                                            padding: "2px 8px",
                                            margin: 0,
                                        }}
                                    >
                                        {label}
                                    </Tag>
                                );
                            })}
                        </div>

                        {!isMobile && (
                            <div
                                style={{
                                    height: "16px",
                                    width: "1px",
                                    backgroundColor: Tokens.hairline,
                                    margin: "0 4px",
                                }}
                            />
                        )}

                        <div
                            style={{
                                display: "flex",
                                gap: "4px",
                                flexWrap: "wrap",
                            }}
                        >
                            {[
                                ["ALL", "All"],
                                ["SPOT", "Spot"],
                                ["EARN", "Earn"],
                                ["AUTO-INVEST", "Auto-Invest"],
                                ["CONVERT", "Convert"],
                                ["FUNDING", "Funding"],
                                ["MARGIN", "Margin"],
                            ].map(([type, label]) => {
                                const isActive = accountTypeFilter === type;
                                return (
                                    <Tag
                                        key={type}
                                        onClick={() => {
                                            setAccountTypeFilter(type);
                                            setLocalPage(1);
                                        }}
                                        style={{
                                            backgroundColor: isActive
                                                ? Tokens.raised
                                                : "transparent",
                                            color: isActive
                                                ? Tokens.textPrimary
                                                : Tokens.textSecondary,
                                            border: isActive
                                                ? `1px solid ${Tokens.accent}`
                                                : `1px solid ${Tokens.hairline}`,
                                            borderRadius: Tokens.radiusTag,
                                            cursor: "pointer",
                                            fontSize: "11px",
                                            fontWeight: isActive ? 500 : 400,
                                            padding: "2px 8px",
                                            margin: 0,
                                        }}
                                    >
                                        {label}
                                    </Tag>
                                );
                            })}
                        </div>

                        <button
                            type="button"
                            data-testid="trades-sort-toggle"
                            onClick={() =>
                                setSortOrder((prev) =>
                                    prev === "desc" ? "asc" : "desc"
                                )
                            }
                            style={{
                                marginLeft: "auto",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "5px",
                                padding: "2px 8px",
                                borderRadius: Tokens.radiusTag,
                                border: `1px solid ${Tokens.hairline}`,
                                backgroundColor: Tokens.card,
                                color: Tokens.textSecondary,
                                fontSize: "11px",
                                fontFamily: Tokens.fontMono,
                                cursor: "pointer",
                            }}
                        >
                            <SwapOutlined rotate={90} />
                            <span>
                                {sortOrder === "desc" ? "Newest" : "Oldest"}
                            </span>
                        </button>
                    </div>
                )}

                {displayedTransactions.length > 0 && isMobile && (
                    <div
                        data-testid="binance-mobile-trade-cards"
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                        }}
                    >
                        {mobileDateGroups.map((group) => (
                            <div key={group.dateLabel}>
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        padding: "2px 4px 6px",
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        color: Tokens.textMuted,
                                        fontFamily: Tokens.fontMono,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.04em",
                                    }}
                                >
                                    <span>{group.dateLabel}</span>
                                    <span>
                                        {group.items.length}{" "}
                                        {group.items.length === 1
                                            ? "trade"
                                            : "trades"}
                                    </span>
                                </div>
                                <div
                                    style={{
                                        backgroundColor: Tokens.card,
                                        border: `1px solid ${Tokens.hairline}`,
                                        borderRadius: Tokens.radiusCard,
                                        overflow: "hidden",
                                    }}
                                >
                                    {group.items.map((record, idx) => (
                                        <TradebookEventRow
                                            key={`${record.id}-${record.time}`}
                                            ev={record}
                                            isLast={
                                                idx === group.items.length - 1
                                            }
                                            isLedgerMode={false}
                                            currencySymbol={currencySymbol}
                                            exchangeRate={1}
                                            showCoin={false}
                                            onCopyQty={copyToClipboard}
                                            rowTestId="binance-mobile-trade-row"
                                            runningBalanceTestId="trade-row-running-balance"
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {displayedTransactions.length > 0 && !isMobile && (
                    <React.Fragment>
                        <Table
                            columns={transactionColumns}
                            dataSource={displayedTransactions}
                            rowKey={(record) =>
                                `${record.tradeId}-${record.time}`
                            }
                            pagination={false}
                            size="small"
                            scroll={{
                                x: "max-content",
                                y: "calc(100vh - 310px)",
                            }}
                            style={{ backgroundColor: Tokens.card }}
                        />
                        <style>
                            {`
                                .ant-table-wrapper .ant-table {
                                    background-color: ${Tokens.card} !important;
                                }
                                .ant-table-wrapper .ant-table-thead > tr > th {
                                    background-color: ${Tokens.raised} !important;
                                    border-bottom: 1px solid ${Tokens.hairline} !important;
                                    color: ${Tokens.textSecondary} !important;
                                    font-weight: 500 !important;
                                    font-size: 12px !important;
                                    padding: 10px 8px !important;
                                    white-space: nowrap !important;
                                }
                                .ant-table-wrapper .ant-table-tbody > tr > td {
                                    background-color: ${Tokens.card} !important;
                                    border-bottom: 1px solid ${Tokens.hairline} !important;
                                    color: ${Tokens.textPrimary} !important;
                                    padding: 10px 8px !important;
                                    white-space: nowrap !important;
                                }
                                .ant-table-wrapper .ant-table-tbody > tr:hover > td {
                                    background-color: ${Tokens.raised} !important;
                                }
                                .ant-table-wrapper .ant-table-container {
                                    border: 1px solid ${Tokens.hairline} !important;
                                    border-radius: 8px;
                                    overflow: hidden;
                                }
                                .ant-pagination-item-active {
                                    background-color: ${Tokens.raised} !important;
                                    border-color: ${Tokens.accent} !important;
                                }
                                .ant-pagination-item-active a {
                                    color: ${Tokens.textPrimary} !important;
                                }
                                .ant-pagination-item a {
                                    color: ${Tokens.textSecondary} !important;
                                }
                            `}
                        </style>
                    </React.Fragment>
                )}

                {filteredTransactions.length === 0 && (
                    <div
                        style={{
                            textAlign: "center",
                            padding: "40px",
                            color: Tokens.textSecondary,
                            fontSize: "14px",
                        }}
                    >
                        No transactions match the selected filters for {symbol}
                    </div>
                )}
            </Spin>
        </Drawer>
    );
};

export default BinanceTransactions;
